import React, { useContext, useEffect, useState } from 'react';
import AppContext from '../../contexts/AppContext';
import tenantApi, { TenantType } from '../../utils/api/tenant';
import { toast } from 'react-toastify';
import ToggleSmall from '../Shared/Toggle/ToggleSmall';

const TENANT_TYPE_OPTIONS: { value: TenantType; label: string }[] = [
  { value: 'regulator', label: 'Regulator' },
  { value: 'builder', label: 'Builder' },
  { value: 'consultant', label: 'Consultant' },
];

const Security = () => {
  const [collapse, setCollapse] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [tenantType, setTenantType] = useState<TenantType | null>(null);
  const [tenantTypeBusy, setTenantTypeBusy] = useState<boolean>(false);
  const appStore = useContext(AppContext);
  const user = appStore.get('user');
  const isSuperAdmin = user?.roleName === 'Super Admin';

  const loadInitialData = () => {
    setBusy(true);
    tenantApi.mfa
      .getMfaEnabled(user.tenantId)
      .then((res) => {
        if (res) {
          setMfaEnabled(res.mfa_enabled || false);
        }
        setBusy(false);
      })
      .catch((e) => {
        console.error('Error loading MFA feature:', e);
        toast.error('Failed to load security settings');
        setBusy(false);
      });
  };

  const loadTenantType = () => {
    tenantApi.tenantType
      .getTenantType(user.tenantId)
      .then((res) => setTenantType(res.tenant_type))
      .catch((e) => {
        console.error('Error loading organization type:', e);
        toast.error('Failed to load organization type');
      });
  };

  useEffect(() => {
    if (user && user.tenantId) {
      loadInitialData();

      if (isSuperAdmin) {
        loadTenantType();
      }
    }
  }, [user?.tenantId]);

  const handleTenantTypeChange = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const nextType = event.target.value as TenantType;
    const previousType = tenantType;

    setTenantType(nextType);
    setTenantTypeBusy(true);

    try {
      await tenantApi.tenantType.editTenantType(user.tenantId, nextType);
      toast.success('Organization type updated successfully!');
      // Re-read the profile so every Permittee/Market/Client label follows
      // the new type without a page reload.
      appStore.reloadInitialData();
    } catch (error) {
      console.error('Error updating organization type:', error);
      toast.error('Failed to update organization type');
      setTenantType(previousType);
    } finally {
      setTenantTypeBusy(false);
    }
  };

  const handleMFAToggle = async () => {
    setBusy(true);

    try {
      await tenantApi.mfa.editMfaEnabled(user.tenantId, { mfa_enabled: !mfaEnabled });
    
      setMfaEnabled(!mfaEnabled);
      toast.success(
        `Multi-Factor Authentication ${!mfaEnabled ? 'enabled' : 'disabled'} successfully!`
      );
    } catch (error) {
      console.error('Error updating MFA setting:', error);
      toast.error('Failed to update Multi-Factor Authentication setting');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>Security</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr />
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: '0',marginLeft:'8px' }}>Enable Multi-Factor Authentication (MFA) Requirement</h3>
          <ToggleSmall
            isChecked={mfaEnabled}
            disabled={busy}
            handleChange={handleMFAToggle}
          />
        </div>
        {isSuperAdmin && tenantType && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <h3 id='tenant-type-label' style={{ marginTop: '0', marginLeft: '8px' }}>Organization Type</h3>
            <select
              id='tenant-type-select'
              aria-labelledby='tenant-type-label'
              value={tenantType}
              disabled={tenantTypeBusy}
              onChange={handleTenantTypeChange}
            >
              {TENANT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <br /><br /><br />
    </div>
  );
};

export default Security;
